import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface SetupInfo {
  tenantId: string;
  role: string;
  tenantName: string;
}

export function CompleteSetup() {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();

  const [setupInfo, setSetupInfo] = useState<SetupInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const checkSetupRequired = useCallback(async () => {
    // Wait for auth to load before checking session
    if (authLoading) {
      return;
    }

    if (!session) {
      // Not authenticated - redirect to login
      navigate("/login");
      return;
    }

    const metadata = session.user.user_metadata;
    const pendingTenantId = metadata?.pending_tenant_id;
    const pendingTenantName = metadata?.pending_tenant_name;
    const pendingRole = metadata?.pending_role;

    if (!pendingTenantId) {
      // No pending setup - redirect to dashboard
      navigate("/");
      return;
    }

    // Check if user already has tenant association (setup already done)
    const { data: existingMembership } = await supabase
      .from("tenant_users")
      .select("id")
      .eq("supabase_user_id", session.user.id)
      .is("deleted_at", null)
      .maybeSingle();

    if (existingMembership) {
      // Already set up, redirect to dashboard
      navigate("/");
      return;
    }

    setSetupInfo({
      tenantId: pendingTenantId,
      role: pendingRole || "owner",
      tenantName: pendingTenantName || "Unknown",
    });
    setLoading(false);
  }, [session, authLoading, navigate]);

  useEffect(() => {
    checkSetupRequired();
  }, [checkSetupRequired]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupInfo || !session) return;

    // Validate password
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // 1. Set password first
      const { error: passwordError } = await supabase.auth.updateUser({
        password: password,
      });

      if (passwordError) {
        throw new Error(`Failed to set password: ${passwordError.message}`);
      }

      // 2. Call complete_owner_setup RPC to create tenant_users record
      const { data, error: setupError } = await supabase.rpc(
        "complete_owner_setup",
      );

      if (setupError) {
        throw new Error(setupError.message);
      }

      if (!data?.success) {
        throw new Error("Setup failed");
      }

      // 3. Refresh session to get new JWT with tenant_id
      await supabase.auth.refreshSession();

      // 4. Redirect to dashboard
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Setup failed");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  if (!setupInfo) {
    return null;
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Complete Your Setup</CardTitle>
          <CardDescription>
            You&apos;ve been invited as <strong>{setupInfo.role}</strong> of{" "}
            <strong>{setupInfo.tenantName}</strong>. Set a password to continue.
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password (min 8 characters)"
                required
                minLength={8}
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                required
                disabled={submitting}
              />
            </div>
          </CardContent>

          <CardFooter>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up...
                </>
              ) : (
                "Complete Setup"
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AuthLayout>
  );
}
